/**
 * Asset loader (asset contract §8.2). Gameplay-owned; exported signatures FROZEN for M1 because
 * the asset lane (materials, environment, lookdev) consumes them.
 *
 * GLTFLoader + meshopt + KTX2, LOD levels, sockets, COL_ extraction. Every failure names the asset id and
 * path; development builds assert that no COL_ node reaches an instance (contract §3.4).
 */
import type { LOD, Material, Mesh, Object3D, Texture, WebGPURenderer } from 'three/webgpu'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js'
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js'
import type { ModelEntry, TextureMap, TextureSetEntry } from '../data/assetSchema'
import { ViewLOD } from '../render/viewLod'
import { dequantizeLevels } from './dequantize'
import { widenNarrowAttributes } from './vertexFormat'
import { assertNoColliderNodes, collectLods, collectSockets, extractColliderData, type ColliderData } from './extract'

export type { ColliderData } from './extract'

/** Basis Universal transcoder, copied from three at P0 (frozen path; LICENSES.md). */
export const BASIS_TRANSCODER_PATH = '/vendor/basis/0.186.1/'

export interface ModelInstance {
  readonly root: Object3D
  readonly lod: LOD
  readonly sockets: ReadonlyMap<string, Object3D>
}

export interface LoadedModel {
  readonly entry: ModelEntry
  readonly collider: ColliderData | null
  /** L-M4 U1: vertex bytes the load-time dequantization added (0 for f32 assets or with the switch off). */
  readonly dequantizedBytes?: number
  /** Shares geometry and materials with every other instance. Call at spawn, never per frame. */
  instantiate(lodScale: number): ModelInstance
}

export interface LoadedTextureSet {
  readonly entry: TextureSetEntry
  readonly maps: Partial<Record<TextureMap, Texture>>
}

export interface AssetLoader {
  loadModel(entry: ModelEntry): Promise<LoadedModel>
  loadTextureSet(entry: TextureSetEntry): Promise<LoadedTextureSet>
  dispose(): void
}

export interface LoaderOptions {
  /**
   * L-M4 U1 (D-924): fold the KHR_mesh_quantization node transform into f32 geometry at load, so mesh-local space is
   * asset metres (materials.json `presets.*.dequantize`, read at load; models are cached, so a preset change mid-session
   * takes effect on the next load). Default true.
   */
  readonly dequantize?: boolean
}

/** Builds a model from a parsed glTF scene. Exported for Node tests (GLTFLoader.parseAsync). */
export function buildLoadedModel(entry: ModelEntry, scene: Object3D, opts?: LoaderOptions): LoadedModel {
  let collider: ColliderData | null
  let levelCount: number
  try {
    collider = extractColliderData(scene, entry.collider)
    levelCount = collectLods(scene).length
  } catch (err: unknown) {
    throw new Error(`${entry.id}: ${messageOf(err)}`, { cause: err })
  }
  if (levelCount !== entry.lod.distances.length) {
    throw new Error(`${entry.id}: GLB has ${levelCount} LODs, entry has ${entry.lod.distances.length}`)
  }
  // Colliders are already extracted (they read geometry × node matrix, so they come out the same either way).
  const dequantizedBytes = opts?.dequantize === false ? 0 : dequantizeLevels(collectLods(scene))
  // One vertex format per attribute (u8 ECL_Mask colours widen to u16), so pipelines three shares stay valid (D-TBD).
  widenNarrowAttributes(scene)
  return {
    entry,
    collider,
    dequantizedBytes,
    instantiate(lodScale: number): ModelInstance {
      const root = scene.clone(true)
      const levels = collectLods(root)
      // SC-6a1: picks its level once per frame from the view camera while the preset pins caster LOD.
      const lod = new ViewLOD()
      lod.name = `${entry.id}:lod`
      for (let i = 0; i < levels.length; i++) {
        levels[i].removeFromParent()
        lod.addLevel(levels[i], entry.lod.distances[i] * lodScale, entry.lod.hysteresis)
      }
      root.add(lod)
      root.name = entry.id
      if (import.meta.env.DEV) assertNoColliderNodes(root, entry.id)
      return { root, lod, sockets: collectSockets(root) }
    },
  }
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

/**
 * The maps of the first material in a texture-set carrier GLB. glTF packs ORM into one texture that
 * GLTFLoader hands out as `roughnessMap`, `metalnessMap` and/or `aoMap`.
 */
export function texturesOf(scene: Object3D): Partial<Record<TextureMap, Texture>> {
  let material: Material | undefined
  scene.traverse((o) => {
    if (!material && (o as Mesh).isMesh) {
      const m = (o as Mesh).material
      material = Array.isArray(m) ? m[0] : m
    }
  })
  const std = material as
    | {
        map?: Texture | null
        normalMap?: Texture | null
        roughnessMap?: Texture | null
        metalnessMap?: Texture | null
        aoMap?: Texture | null
        emissiveMap?: Texture | null
      }
    | undefined
  const maps: Partial<Record<TextureMap, Texture>> = {}
  if (std?.map) maps.baseColor = std.map
  if (std?.normalMap) maps.normal = std.normalMap
  const orm = std?.roughnessMap ?? std?.metalnessMap ?? std?.aoMap
  if (orm) maps.orm = orm
  if (std?.emissiveMap) maps.emissive = std.emissiveMap
  return maps
}

/** The maps an entry promises that the carrier does not have. */
export function missingMaps(entry: TextureSetEntry, maps: Partial<Record<TextureMap, Texture>>): TextureMap[] {
  return entry.maps.filter((m) => maps[m] === undefined)
}

async function loadGltf(gltf: GLTFLoader, id: string, path: string): Promise<Object3D> {
  try {
    return (await gltf.loadAsync(path)).scene
  } catch (err: unknown) {
    throw new Error(`${id}: could not load ${path}: ${messageOf(err)}`, { cause: err })
  }
}

/** Call after `await renderer.init()`. */
export function createAssetLoader(renderer: WebGPURenderer, opts?: LoaderOptions): AssetLoader {
  const ktx2 = new KTX2Loader().setTranscoderPath(BASIS_TRANSCODER_PATH).detectSupport(renderer)
  const gltf = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).setKTX2Loader(ktx2)
  return {
    async loadModel(entry: ModelEntry): Promise<LoadedModel> {
      return buildLoadedModel(entry, await loadGltf(gltf, entry.id, entry.path), opts)
    },
    async loadTextureSet(entry: TextureSetEntry): Promise<LoadedTextureSet> {
      const maps = texturesOf(await loadGltf(gltf, entry.id, entry.path))
      const missing = missingMaps(entry, maps)
      if (missing.length > 0) throw new Error(`${entry.id}: ${entry.path} lacks the ${missing.join(', ')} map(s) its entry lists`)
      // Anisotropy is set by the material library per quality preset.
      return { entry, maps }
    },
    dispose(): void {
      ktx2.dispose()
    },
  }
}
